import React, { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import Table from "../../Components/TableDesign";
import SearchBar from "../../Components/SearchBar";
import { GET, TenantID } from "../dataSaver";
import { currentUser } from "../../utils/auth";
import style from "./index.module.scss";
import { Icon, Intent } from "@blueprintjs/core";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import EditIcon from "@mui/icons-material/Edit";
import Loader from "../../images/loading.gif";
import { useNavigate, useLocation } from "react-router-dom";
import { ErrorToaster } from "../../utils/toaster";
import AddSiteSelectionPopup from "./AddSiteSelectionPopup";
import { useReactToPrint } from "react-to-print";

const TimesheetAlertsTable = ({
  reminderType: propReminderType,
  inModal = false,
}) => {
  const [searchKey, setSearchKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [filteredSites, setFilteredSites] = useState([]);
  const [openAlert, setOpenAlert] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [alertData, setAlertData] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedSite, setSelectedSite] = useState(null);
  const [showSiteSelection, setShowSiteSelection] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(10);

  // Get reminderType with proper priority
  const reminderTypeFromState = location.state?.reminderType;
  const reminderTypeFromQuery = new URLSearchParams(location.search).get(
    "reminderType"
  );
  const reminderType =
    propReminderType ||
    reminderTypeFromState ||
    reminderTypeFromQuery ||
    "TIMESHEET_CREATION_REMINDER";

  // Log the final reminderType being used
  console.log("Using reminderType:", reminderType);

  // Get selected site from location state
  const selectedSiteFromLocation = location.state?.selectedSite;
  const isMultiSiteEntity =
    sessionStorage.getItem("isMultiSiteEntity") === "true";

  const [debouncedSearchKey, setDebouncedSearchKey] = useState("");
  const componentRef = useRef(null);
  const loggedInUser = currentUser();
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchKey(searchKey);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchKey]);

  useEffect(() => {
    if (!reminderType) {
      if (inModal) {
        navigate(
          "/alerts-notifications?view=alertsTable&reminderType=TIMESHEET_CREATION_REMINDER"
        );
      } else {
        navigate(
          "/alerts-notifications/timesheet-alerts-table?reminderType=TIMESHEET_CREATION_REMINDER"
        );
      }
      return;
    }
    fetchSiteAlerts();
  }, [selectedSite, page, reminderType, searchKey]);

  const fetchSiteAlerts = async () => {
    try {
      setIsLoading(true);

      if (!reminderType) {
        console.error("No reminder type specified");
        ErrorToaster("Invalid reminder type");
        return;
      }

      // Update API URL to include pagination parameters
      let url = `timesheet-management-service/reminder?reminderType=${reminderType}&tenantId=${TenantID}&offset=${
        page - 1
      }&limit=${limit}`;

      if (isMultiSiteEntity && selectedSite?.id) {
        url += `&siteId=${selectedSite.id}`;
      }

      if (searchKey) {
        url += `&searchText=${searchKey}`;
      }

      console.log("Fetching alerts with:", {
        reminderType,
        selectedSite,
        url,
      });

      const { data } = await GET(url);
      console.log("API Response:", data);

      // Update total count from API response
      setTotalCount(data?.noElements);

      // Helper function to convert API value to actual hours
      const getActualHours = (value) => value * 24;

      // Transform the data for table display
      const transformedData =
        data?.reminderData
          ?.filter((alert) => alert.site?.id && alert.site?.name)
          ?.map((alert) => ({
            id: alert.id,
            name: alert.site.name,
            reminders:
              reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER"
                ? alert.reminderNotificationAvaliable
                  ? "ENABLED"
                  : "DISABLED"
                : alert.reminderDetails
                    ?.map(
                      (r) =>
                        `${r.reminderSchedule.days} ${
                          r.reminderSchedule.days === 1 ? "DAY" : "DAYS"
                        }`
                    )
                    .join(", ") || "-",
            expirationAlert:
              reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER"
                ? alert.reviewReminderDetails?.reminderAvailable
                  ? `${getActualHours(
                      alert.reviewReminderDetails.reminderSchedule.days
                    )} Hours`
                  : "DISABLED"
                : alert.alertDetails?.reminderAvailable
                ? `${getActualHours(
                    alert.alertDetails.reminderSchedule.days
                  )} Hours`
                : "DISABLED",
            expirationNotification:
              reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER"
                ? alert.pastDueNotificationDetails?.reminderAvailable
                  ? "ENABLED"
                  : "DISABLED"
                : alert.expirationNotificationDetails?.reminderAvailable
                ? "ENABLED"
                : "DISABLED",
            lastUpdatedBy: alert.lastUpdatedUser
              ? `${alert.lastUpdatedUser.name?.firstName || ""} ${
                  alert.lastUpdatedUser.name?.lastName || ""
                }`.trim()
              : "-",
            lastUpdated: alert.lastModifiedDate
              ? format(new Date(alert.lastModifiedDate), "MMM dd, yyyy")
              : "-",
            isActive: true,
            originalData: alert,
          })) || [];

      setFilteredSites(transformedData);
    } catch (error) {
      console.error("Error fetching alerts:", error);
      ErrorToaster("Error fetching alerts");
      setFilteredSites([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (debouncedSearchKey) {
      const filtered = filteredSites.filter((site) =>
        site.name.toLowerCase().includes(debouncedSearchKey.toLowerCase())
      );
      setFilteredSites(filtered);
    } else {
      fetchSiteAlerts();
    }
  }, [debouncedSearchKey]);

  const getSearchKey = (value) => {
    setSearchKey(value);
  };
  const onClickFunction = (data) => {
    if (inModal) {
      navigate(
        `/alerts-notifications?view=alerts&reminderType=${reminderType}`,
        {
          state: {
            edit: true,
            alertData: data.originalData,
            selectedSite: data.originalData.site,
            reminderType,
          },
        }
      );
    } else {
      navigate(
        `/alerts-notifications/timesheet-alerts?reminderType=${reminderType}`,
        {
          state: {
            edit: true,
            alertData: data.originalData,
            selectedSite: data.originalData.site,
            reminderType,
          },
        }
      );
    }
  };

  const getTableDataValues = () => {
    if (!filteredSites?.length) {
      return Array(7).fill({ type: "text", value: [] });
    }

    return [
      {
        type: "dot",
        value: filteredSites.map((site) => (site.isActive ? "green" : "grey")),
        tooltipValue: filteredSites.map((site) =>
          site.isActive ? "Active" : "Inactive"
        ),
      },
      {
        type: "text",
        value: filteredSites.map((site) => site.name),
        onClickFunction,
      },
      {
        type: "textWithHover",
        value: filteredSites.map((site) => {
          // Handle cases where reminders might be undefined or not a string
          if (!site.reminders) return "-";
          const reminderArray = Array.isArray(site.reminders)
            ? site.reminders
            : site.reminders.split(", ");
          // Just show the first reminder regardless of how many there are
          return reminderArray[0] || "-";
        }),
        hoverText: filteredSites.map((site) => {
          // Handle cases where reminders might be undefined or not a string
          if (!site.reminders) return ["-"];
          const reminderArray = Array.isArray(site.reminders)
            ? site.reminders
            : site.reminders.split(", ");
          // Return array of reminders to show as list
          return reminderArray;
        }),
      },
      {
        type: "text",
        value: filteredSites.map((site) => site.expirationAlert),
      },
      {
        type: "text",
        value: filteredSites.map((site) => site.expirationNotification),
      },
      { type: "text", value: filteredSites.map((site) => site.lastUpdatedBy) },
      { type: "text", value: filteredSites.map((site) => site.lastUpdated) },
    ];
  };

  const handleNotificationSetup = () => {
    if (isMultiSiteEntity) {
      setShowSiteSelection(true);
    } else {
      if (inModal) {
        navigate(
          `/alerts-notifications?view=alerts&reminderType=${reminderType}`,
          {
            state: { reminderType },
          }
        );
      } else {
        navigate(
          `/alerts-notifications/timesheet-alerts?reminderType=${reminderType}`,
          {
            state: { reminderType },
          }
        );
      }
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const tableHeaders = [
    "",
    "Site",
    "Reminders After Last Day",
    "Period Expiration Alert",
    "Period Expiration Notification",
    "Last Updated By",
    "Last Updated",
  ];

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `TimesheetAlerts_${format(new Date(), "MM_dd_yy")}`,
    removeAfterPrint: true,
  });

  return (
    <div className={style.tableContainer}>
      {selectedSite && (
        <div className={style.selectedSiteInfo}>
          <span>Selected Site: {selectedSite.name}</span>
        </div>
      )}

      <div className={style.headerSection}>
        <div className={style.searchSection}>
          <SearchBar
            getSearchKey={getSearchKey}
            searchKey={searchKey}
            className={style.searchBarStyle}
            placeholder="Search here.."
          />
          <PrintOutlinedIcon
            className={style.printIcon}
            onClick={handlePrint}
            style={{ cursor: "pointer" }}
          />
          <button
            className={style.notificationSetup}
            onClick={handleNotificationSetup}
          >
            NOTIFICATION SETUP
          </button>
        </div>
      </div>

      <div className={style.responsiveTable}>
        <div className={style.tableWrapper} ref={componentRef}>
          {isLoading ? (
            <div className={style.loaderContainer}>
              <img src={Loader} alt="Loading" width={250} />
            </div>
          ) : (
            <Table
              tableHeaderValues={tableHeaders}
              tableDataValues={getTableDataValues()}
              tableData={filteredSites}
              gridStyle={`${style.alertsTableGrid}`}
              heading={
                filteredSites.length === 0
                  ? "There are no Timesheet Alerts for you to manage"
                  : ""
              }
              subHeading={
                "To add a new Timesheet Alert click on Notification Setup button above"
              }
              hidePagination={false}
              getSelectedPage={handlePageChange}
              totalCount={totalCount}
              page={page}
              wrapHeaders={true}
            />
          )}
        </div>
      </div>

      {showSiteSelection && (
        <AddSiteSelectionPopup
          show={showSiteSelection}
          handleClose={() => setShowSiteSelection(false)}
          handleSelectedSite={(site) => {
            setSelectedSite(site);
            if (inModal) {
              navigate(
                `/alerts-notifications?view=alerts&reminderType=${reminderType}`,
                {
                  state: { selectedSite: site, reminderType },
                }
              );
            } else {
              navigate(`/alerts-notifications/timesheet-alerts`, {
                state: { selectedSite: site, reminderType },
              });
            }
          }}
          reminderType={reminderType}
        />
      )}
    </div>
  );
};

export default TimesheetAlertsTable;
